/// <amd-module name="Analytics/Boards/VelocityQueries/CompletedLateMetastateByIterationsQuery" />
import { AggregationMode, Metastate, ModefulValueSetting } from "Analytics/Boards/BoardsQueries/DataContracts";
import { Iteration } from "Analytics/Boards/BoardsQueries/IterationsQuery";
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { QueryUtilities } from "Analytics/Common/Utilities/QueryUtilities";
import { latestODataVersion } from "Analytics/Common/Utilities/QueryUtilities";
import { VelocityDataHelper } from "./VelocityDataHelper";
import { addDays, VelocityQueryBase } from "./VelocityQueryBase";

// Analogous to Tfs/Service/WebAccess/Widgets/Scripts/Velocity/Queries/CompletedLateMetastateByIterationsQuery.ts
export class CompletedLateMetastateByIterationsQuery extends VelocityQueryBase<Metastate[]> {
    private static generateQueryOptions(
        projectId: string,
        teamId: string,
        iterations: Iteration[],
        aggregation: ModefulValueSetting<AggregationMode, string>,
        workItemTypes: string[],
        showResolvedItemsAsCompleted = false,
        delay?: number
    ): ODataQueryOptions {
        const iterationAndCompletedDateClause = CompletedLateMetastateByIterationsQuery.generateIterationAndCompletedDateClause(iterations, delay);
        const workItemTypeClause = QueryUtilities.isInArray("WorkItemType", workItemTypes, true /* useQuotes */);

        let $apply =
            `filter(` +
            `Teams/any(t:t/TeamSK eq ${teamId})` +
            ` and (${workItemTypeClause})` +
            ` and (StateCategory eq 'Completed'`;
            

        if (showResolvedItemsAsCompleted) {
            $apply += 
                ` or StateCategory eq 'Resolved'` +
                ` or State eq 'Resolved'`;
        }

        $apply += `) and ${iterationAndCompletedDateClause}`;

        const aggregationClause = VelocityDataHelper.getQueryAggregation(aggregation);
        $apply += `)/groupby((StateCategory, State, IterationSK),${aggregationClause})`;

        return {
            entityType: "WorkItems",
            oDataVersion: latestODataVersion,
            project: projectId,
            $apply: $apply
        };
    }

    private static getDelayedDateTimeOffset(iterationEndDateTimeOffset: string, delay: number): string {
        const parsedDateTimeOffset = new Date(iterationEndDateTimeOffset);
        const delayedDate = addDays(parsedDateTimeOffset, delay);
        const delayedDateString = delayedDate.toISOString();

        return delayedDateString;
    }

    public static generateIterationAndCompletedDateClause(iterations: Iteration[], delay?: number) {
        let iterationAndCompletedDateClause = "";
        if (delay == null || delay === 0) {
            const isInAnyIteration = QueryUtilities.isInArray(
                "IterationSK",
                iterations.filter(x => x.IterationSK).map(iteration => iteration.IterationSK)
            );

            iterationAndCompletedDateClause = `(${isInAnyIteration}) and (CompletedDate gt Iteration/EndDate or StateChangeDate gt Iteration/EndDate)`;
        } else {
            const isInAnyIterationAndCompletedAfterDelayedDate = QueryUtilities.isInArray(
                "(IterationSK",
                iterations
                    .filter(x => x.EndDateTimeOffset)
                    .map(iteration => {
                        const delayedDateTimeOffsetString = CompletedLateMetastateByIterationsQuery.getDelayedDateTimeOffset(
                            iteration.EndDateTimeOffset,
                            delay
                        );
                        return `${iteration.IterationSK} and (CompletedDate gt ${delayedDateTimeOffsetString} or StateChangeDate gt ${delayedDateTimeOffsetString}))`;
                    })
            );

            iterationAndCompletedDateClause = `(${isInAnyIterationAndCompletedAfterDelayedDate})`;
        }
        return iterationAndCompletedDateClause;
    }
    // Used by shadow testing to differentiate fake from real velocity widgets.
    private queryName?: string;
    constructor(
        command: string,
        projectId: string,
        teamId: string,
        iterations: Iteration[],
        aggregation: ModefulValueSetting<AggregationMode, string>,
        workItemTypes: string[],
        showResolvedItemsAsCompleted = false,
        delay?: number,
        queryName?: string
    ) {
        super(
            command,
            CompletedLateMetastateByIterationsQuery.generateQueryOptions(projectId, teamId, iterations, aggregation, workItemTypes, showResolvedItemsAsCompleted, delay)
        );
        this.queryName = queryName;
    }

    public getQueryName(): string {
        return this.queryName || "CompletedLateMetastateByIterationsQuery";
    }

    protected interpretQueryResults(data: { value: Metastate[] }): Metastate[] {
        // This null filter is only needed because we don't include a 'ne null' filter for effort in the query.
        // As of this writing the reason we don't include it in the query is to get better SQL perf.
        // That may or may not change in the future.
        return data.value.filter(metastate => metastate.AggregationResult != null);
    }
}
